/**
 * Custom UI Utils
 */
const cuiUtils = {
    getComputed: {
        transitionDuration(element) {
            // transition-duration 값을 숫자로 변환 (ms)
            return parseFloat(window.getComputedStyle(element).transitionDuration) * 1000;
        },
        height(element) {
            return parseFloat(window.getComputedStyle(element).height);
        },
        zIndex(element) {
            return window.getComputedStyle(element).zIndex;
        },
    },

    getTransitionDuration(element) {
        return cuiUtils.getComputed.transitionDuration(element) ?? 0;
    },

    a11yKey(params = {}, event) {
        // 키보드 접근성
        const target = event.target;

        const defaultOptions = {
            type: 'default', // 'trap' or 'default'
            callback: null,
        };
        const options = { ...defaultOptions, ...params };

        const isVisible = (element) => {
            return element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
        };

        const root = event.currentTarget;
        const focusableElements = [
            ...root.querySelectorAll('a, button, input, select, details, textarea, [tabindex]:not([tabindex="-1"])'),
        ].filter(isVisible);
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const keyTab = event.key === 'Tab' || event.keyCode === 9;
        const keyShift = event.shiftKey;
        const isTabPressed = keyTab && !keyShift;
        const isShiftTabPressed = keyShift && keyTab;

        if (options.type === 'trap') {
            // kayboard trap
            if (target === lastElement && isTabPressed) {
                event.preventDefault();
                setTimeout(() => firstElement.focus(), 50);
            } else if (target === firstElement && isShiftTabPressed) {
                event.preventDefault();
                setTimeout(() => lastElement.focus(), 50);
            }
        } else {
            // default
            if (target === lastElement && isTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            } else if (target === firstElement && isShiftTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            }
        }
    },

    openSlide(slideElement, params = {}) {
        let isVisible = false;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = 0;
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.useAttrHidden) {
            slideElement.hidden = false;
        } else {
            slideElement.style.display = 'block';
        }

        requestAnimationFrame(() => {
            isVisible = true;

            // slideElement.offsetHeight;
            slideElement.style.height = slideElement.scrollHeight + 'px';
            if (options.activeElement) options.activeElement.classList.add(options.activeClass);

            if (typeof options.on.slideStart === 'function') {
                options.on.slideStart();
            }

            setTimeout(() => {
                if (isVisible && (options.activeElement && options.activeElement.classList.contains(options.activeClass))) {
                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }
                }
            }, options.duration);
        });
    },

    closeSlide(slideElement, params = {}) {
        let isVisible = true;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = slideElement.scrollHeight + 'px';
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.activeElement) options.activeElement.classList.remove(options.activeClass);

        if (typeof options.on.slideStart === 'function') {
            options.on.slideStart();
        }

        requestAnimationFrame(() => {
            slideElement.style.height = 0;

            setTimeout(() => {
                if (isVisible && (options.activeElement && !options.activeElement.classList.contains(options.activeClass))) {
                    if (options.useAttrHidden) {
                        slideElement.hidden = true;
                    } else {
                        slideElement.style.display = 'none';
                    }

                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';
                    slideElement.style.display = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }

                    isVisible = false;
                }
            }, options.duration);
        });
    }
}


/**
 * GNB - PC
 */
class CustomHeader extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            header: 'custom-header',
            navMenus: 'custom-header__nav',
            headerGroup: 'custom-header-group',
            announcementBar: 'announcement-bar',
            breadcrumb: 'breadcrumbs',
            state: {
                active: 'is-active',
                minimized: 'is-minimized',
            },
        };

        this.isScrolling = false;
        this.onScrollHandle = this.onScroll.bind(this);
        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    get backdrop() {
        return document.getElementById(`${this.classes.header}-backdrop`);
    }

    get headerGroup() {
        return this.closest(`.${this.classes.headerGroup}`);
    }

    get sideNav() {
        const getId = this.buttonMobileMenu.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get topSearch() {
        const getId = this.buttonSearch.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get announcementBar() {
        return this.headerGroup && this.headerGroup.querySelector(`.${this.classes.announcementBar}`);
    }

    get breadcrumb() {
        return document.querySelector(`.${this.classes.breadcrumb}`);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onScroll();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.header = this;
        this.navMenus = this.header.querySelector(`.${this.classes.navMenus}`);
        this.buttonMobileMenu = this.header.querySelector('[data-mobile-menu="open"]');
        this.buttonSearch = this.header.querySelector('[data-search-menu="open"]');

        if (!this.backdrop) {
            const createBackdrop = document.createElement('div');
            createBackdrop.id = `${this.classes.header}-backdrop`;
            createBackdrop.classList.add('backdrop');
            document.body.prepend(createBackdrop);
        }
    }

    initEventListeners() {
        this.navMenus.addEventListener('mouseenter', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('focus', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('touchstart', this.onEnterMenu.bind(this));
        this.navMenus.addEventListener('mouseleave', this.onLeaveNavMenus.bind(this));
        this.navMenus.addEventListener('keydown', cuiUtils.a11yKey.bind(this, { callback: () => this.onLeaveNavMenus() }));
        window.addEventListener('scroll', this.onScrollHandle);
        window.addEventListener('resize', this.onScrollHandle);

        this.buttonMobileMenu?.addEventListener('click', this.onDrawSideNav.bind(this));
        this.buttonSearch?.addEventListener('click', this.onDrawSearch.bind(this));
    }

    removeEventListeners() {
        window.removeEventListener('scroll', this.onScrollHandle);
        window.removeEventListener('resize', this.onResizeHandle);
        this.removeEventBackdrop();
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    initActiveState() {
        const prev = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);
        
        if (prev) {
            prev.classList.remove(this.classes.state.active);
            this.header.classList.remove(this.classes.state.active);

            const subDepth = prev.querySelector('.nav__subwrap');
            if (subDepth) {
                subDepth.hidden = true;
                this.backdrop?.classList.remove(this.classes.state.active);
            }
        }
    }

    onEnterMenu(e) {
        const target = e.target;

        if (target.closest('.nav__depth1__link')) {
            if (this.topSearch.hidden === false) {
                // 상단 검색바 열려있을 경우 닫기
                this.topSearch.onCloseSearch();
            }

            this.initActiveState();
            const item = target.closest('li');
            const subDepth = item.querySelector('.nav__subwrap');
            if (subDepth) {
                e.preventDefault(); // GNB 하위뎁스 노출되기 전에 링크 이동되는 것 방지 - 태블릿 touchstart 고려

                subDepth.hidden = false;
                subDepth.offsetHeight;
                this.backdrop?.classList.add(this.classes.state.active);
                this.backdrop?.addEventListener('click', this.onCloseBackdropHandle); // backdrop 이벤트 리스너 추가 - search backdrop과 중복 호출 방지
            } else {
                this.backdrop?.classList.remove(this.classes.state.active);
            }

            item.classList.add(this.classes.state.active);
            this.header.classList.add(this.classes.state.active);
        }
    }

    onLeaveNavMenus() {
        this.initActiveState();
    }

    onCloseBackdrop() {
        this.onLeaveNavMenus();
        this.removeEventBackdrop(); // backdrop 이벤트 리스너 제거 - search backdrop과 중복 호출 방지
    }

    onDrawSideNav() {
        if (this.topSearch.hidden === false) {
            // 상단 검색바 열려있을 경우 닫기
            this.topSearch.onCloseSearch();
        };
        
        // 모바일 메뉴
        this.sideNav.hidden = false;
        this.buttonMobileMenu.setAttribute('aria-expanded', true);
        this.header.classList.add(this.classes.state.active);
        document.documentElement.classList.add('custom-scroll-lock');

        const buttonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        setTimeout(() => {
            this.sideNav.classList.add('is-open');
            this.sideNav.querySelector('.custom-sidenav__wrap').addEventListener('keydown', (event) => {
                cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
            });
            if (buttonClose) buttonClose.focus();
        }, 10);
    }

    onDrawSearch() {
        if (this.sideNav.hidden === false) {
            // 모바일 메뉴 열려있을 경우 닫기
            this.sideNav.onCloseNav();
        };

        // 상단 검색바
        if (this.buttonSearch.classList.contains(this.classes.state.active)) {
            this.onCloseSearch();
            return;
        }
        
        if (this.backdrop) {
            this.backdrop.style.zIndex = cuiUtils.getComputed.zIndex(this.topSearch);
            this.backdrop.classList.add(this.classes.state.active);
        }
        this.header.classList.add(this.classes.state.active);
        
        const buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
        const duration = cuiUtils.getTransitionDuration(this.topSearch);
        cuiUtils.openSlide(this.topSearch, {
            activeElement: this.buttonSearch,
            duration: duration,
            on: {
                slideStart: () => {
                    this.topSearch.classList.add('is-open');
                    this.buttonSearch.setAttribute('aria-expanded', true);
                },
                slideEnd: () => {
                    this.topSearch.addEventListener('keydown', (event) => {
                        cuiUtils.a11yKey({
                            type: 'default',
                            callback: () => {
                                this.onCloseSearch();
                            }
                        }, event); // 키보드 접근성
                    });
                    if (buttonClose) buttonClose.focus();
                }
            }
        });
    }

    onCloseSearch() {
        this.topSearch.onCloseSearch();
    }

    onScroll() {
        if (this.isScrolling) return;

        this.isScrolling = true;
        requestAnimationFrame(() => {
            const scrY = window.scrollY;
            const startY = 0;
            const headerStartY = this.header.offsetTop;
            
            const setPositionSideNav = (h) => {
                // 모바일메뉴 위치 조정
                const headerHeight = cuiUtils.getComputed.height(this.header);
                const announcementBarHeight = h ?? 0;

                return this.sideNav.style.setProperty('--sidevnav-top', `${headerHeight + announcementBarHeight}px`);
            }

            const setPositionBreadcrumb = () => {
                // breadcrumb 위치 조정
                const isFixedHeader = window.getComputedStyle(this.headerGroup).position === 'fixed';

                if (this.breadcrumb && isFixedHeader) {
                    this.breadcrumb.style.setProperty('--breadcrumb-top', `${cuiUtils.getComputed.height(this.headerGroup)}px`)
                }
            };

            if (scrY > startY) {
                this.header.classList.add(this.classes.state.minimized);
                setPositionBreadcrumb();
                
                if (this.headerGroup && this.announcementBar) {
                    // 상단 알림바 노출되고 있을 경우
                    this.headerGroup.style.transform = `translateY(-${headerStartY + 1}px)`;
                    setPositionSideNav(-1);
                }
            } else {
                this.header.classList.remove(this.classes.state.minimized);
                setPositionBreadcrumb();

                if (this.headerGroup && this.announcementBar) {
                    this.headerGroup.style.transform = `translateY(0)`;
                    setPositionSideNav(headerStartY);
                }
            }
            this.isScrolling = false;
        });
    }
}
customElements.define('custom-header', CustomHeader);


/**
 * GNB - Mobile
 */
class CustomMobileSideNav extends HTMLElement {
    constructor() {
        super();

        this.attachShadow({ mode: 'open' });
        this.shadowRoot.innerHTML = `
            <slot></slot>
            <div part="backdrop"></div>
        `;

        this.classes = {
            sideNav: 'custom-sidenav',
            navMenus: 'custom-sidenav__nav',
            header: 'custom-header',
            state: {
                active: 'is-active',
                current: 'is-current'
            },
        };
    }

    get backdrop() {
        return this.shadowRoot.querySelector('[part="backdrop"]');
    }

    get languagePopup() {
        const getId = this.ButtonLang.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onMediaQuery();
    }

    initElements() {
        this.sideNav = this;
        this.ButtonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        this.ButtonLang = this.sideNav.querySelector('[data-mobile-language="open"]');
        this.navMenus = this.sideNav.querySelector(`.${this.classes.navMenus}`);
        this.header = document.querySelector(`.${this.classes.header}`);
    }

    initEventListeners() {
        this.navMenus.addEventListener('click', this.onEnterMenu.bind(this), true);
        this.backdrop?.addEventListener('click', this.onCloseNav.bind(this));
        this.ButtonClose?.addEventListener('click', this.onCloseNav.bind(this));
        this.ButtonLang?.addEventListener('click', this.onDrawLanguage.bind(this));
    }

    initActiveState() {
        this.clearActiveState(1);
    }

    clearActiveState(depth) {
        let prevItem;

        switch (depth) {
            case 1:
                prevItem = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);
                break;
            case 2:
                prevItem = this.navMenus.querySelector(`.nav__depth2 >li.${this.classes.state.active}`);
                break;
            default:
                break;
        }

        if (prevItem) {
            const subDepth = prevItem.querySelector('.nav__subwrap');
            if (subDepth) {
                this.closeMenu(prevItem, subDepth);
            }
        }
    }

    openMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        const button = item.querySelector('.nav__collapsible');

        cuiUtils.openSlide(subDepth, {
            activeElement: item,
            duration: duration,
            on: {
                slideEnd: () => {
                    button.setAttribute('aria-expanded', true);
                }
            }
        });
    }
    
    closeMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        const button = item.querySelector('.nav__collapsible');
        
        cuiUtils.closeSlide(subDepth, {
            activeElement: item,
            duration: duration,
            on: {
                slideEnd: () => {
                    button.setAttribute('aria-expanded', false);
                }
            }
        });
    }

    onEnterMenu(e) {
        const target = e.target;

        const collapse = (target, subWrapSelector, callback) => {
            const item = target.closest('li');
            const subDepth = item.querySelector(subWrapSelector);

            if (subDepth) {
                e.preventDefault();

                if (item.classList.contains(this.classes.state.active)) {
                    this.closeMenu(item, subDepth);
                } else {
                    if (typeof callback === 'function') callback();
                    this.openMenu(item, subDepth);
                }
            }
        };

        if (target.closest('.nav__collapsible')) {
            // 1depth
            collapse(target, '.nav__subwrap', () => {
                this.clearActiveState(1);
            });
        }
    }

    onCloseNav() {
        if (this.sideNav.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-mobile-menu="open"]');
        const sideNavInner = this.sideNav.querySelector(`.${this.classes.sideNav}__wrap`);
        const duration = cuiUtils.getTransitionDuration(sideNavInner);
        
        this.onCloseLanguage();
        this.sideNav.classList.remove('is-open');
        this.header.classList.remove(this.classes.state.active);
        document.documentElement.classList.remove('custom-scroll-lock');
        setTimeout(() => {
            this.sideNav.hidden = true;
            headerbutton.setAttribute('aria-expanded', false);
            headerbutton.focus();
            this.initActiveState();
        }, duration);
    }

    onDrawLanguage() {
        this.ButtonLang.setAttribute('aria-expanded', true);
        this.languagePopup.classList.add('is-open');
        this.languagePopup.addEventListener('keydown', (event) => {
            cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
        });
        
        const buttonClose = this.languagePopup.querySelector('[data-mobile-language="close"]');
        if (buttonClose) {
            buttonClose.focus();
            buttonClose.addEventListener('click', this.onCloseLanguage.bind(this));
        }
    }

    onCloseLanguage() {
        if (!this.languagePopup && !this.languagePopup.classList.contains('is-open')) return;

        this.languagePopup.classList.remove('is-open');
        this.ButtonLang.setAttribute('aria-expanded', false);
        this.ButtonLang.focus();
    }

    onMediaQuery() {
        const mm = {
            desktop: window.matchMedia('(min-width: 1281px)'),
        };

        const updateMatches = () => {
            if (mm.desktop.matches) {
                this.onCloseNav();
            }
        };

        updateMatches();
        mm.desktop.addEventListener('change', updateMatches);
    }
}
customElements.define('custom-mobile-sidenav', CustomMobileSideNav);


/**
 * Top Search
 */
class CustomTopSearch extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            topSearch: 'custom-top-search',
            inputSearch: 'custom-top-search__input--text',
            header: 'custom-header',
            state: {
                active: 'is-active',
            },
        };

        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
    }

    disconnectedCallback() {
        this.removeEventBackdrop();
    }

    initElements() {
        this.topSearch = this;
        this.buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
        this.inputSearch = this.topSearch.querySelector(`.${this.classes.inputSearch}`);
        this.header = document.querySelector(`.${this.classes.header}`);
        this.backdrop = document.getElementById(`${this.classes.header}-backdrop`);

        this.onChangeValue();
    }

    initEventListeners() {
        this.buttonClose?.addEventListener('click', this.onCloseSearch.bind(this));
        this.inputSearch?.addEventListener('input', this.onChangeValue.bind(this));
        this.backdrop?.addEventListener('click', this.onCloseBackdropHandle);
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    onChangeValue() {
        const button = this.topSearch.querySelector('.custom-top-search__button--clear');
        button.addEventListener('click', () => {
            button.style.display = '';
            this.inputSearch.value = '';
            this.inputSearch.focus();
        });

        if (this.inputSearch.value !== '') {
            button.style.display = 'block';
        } else {
            button.style.display = '';
        }
    }

    onCloseSearch() {
        if (this.topSearch.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-search-menu="open"]');
        const duration = cuiUtils.getTransitionDuration(this.topSearch);
        cuiUtils.closeSlide(this.topSearch, {
            activeElement: headerbutton,
            duration: duration,
            on: {
                slideStart: () => {
                    this.topSearch.classList.remove('is-open');
                    this.header.classList.remove(this.classes.state.active);
                    
                    if (this.backdrop) {
                        this.backdrop.classList.remove(this.classes.state.active);
                        this.backdrop.style = '';
                    }
                },
                slideEnd: () => {
                    headerbutton.setAttribute('aria-expanded', false);
                    headerbutton.focus();
                }
            }
        });
    }

    onCloseBackdrop() {
        this.onCloseSearch();
    }
}
customElements.define('custom-top-search', CustomTopSearch);


/**
 * Footer
 */
class CustomFooter extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            footer: 'custom-footer',
            goTopButton: 'custom-footer-gotop',
            state: {
                active: 'is-active',
            },
        };
    }

    get goTopButton() {
        return document.querySelector(`.${this.classes.goTopButton}`);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onScroll();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.footer = this;
    }

    initEventListeners() {
        this.goTopButton?.addEventListener('click', this.onPageTop.bind(this));
        window.addEventListener('scroll', this.onScroll.bind(this));
    }

    removeEventListeners() {
        window.removeEventListener('scroll', this.onScroll.bind(this));
    }

    onPageTop(e) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onScroll() {
        if (!this.goTopButton) return;

        requestAnimationFrame(() => {
            const scrY = window.scrollY;
            const startY = window.innerHeight / 2;
            const offsetY = Math.ceil(scrY + window.innerHeight);
            const footerStartY = this.footer.offsetTop;

            if (scrY > startY) {
                this.goTopButton.classList.add(this.classes.state.active);
                if (offsetY >= footerStartY) {
                    this.goTopButton.classList.add('is-unfixed');
                } else {
                    this.goTopButton.classList.remove('is-unfixed');
                }
            } else {
                this.goTopButton.classList.remove(this.classes.state.active);
            }
        });
    }
}
customElements.define('custom-footer', CustomFooter);


/**
 * Dropdown List
 */
class CustomUIDropdown extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            button: 'custom-ui-dropdown__button',
            options: 'custom-ui-dropdown__options',
            state: {
                active: 'is-active',
                open: 'is-open',
                reverse: 'is-reverse'
            },
        };
    }

    get slideMode() {
        return this.getAttribute('slide');
    }

    get selectMode() {
        return this.hasAttribute('select');
    }

    connectedCallback() {
        this.button = this.querySelector(`.${this.classes.button}`);
        this.options = this.querySelector(`.${this.classes.options}`);
        this.setValue();

        document.addEventListener('mousedown', this.onLeave.bind(this));
        this.addEventListener('click', this.onEnter.bind(this));
    }

    disconnectedCallback() {
        document.removeEventListener('mousedown', this.onLeave.bind(this));
    }

    setTranslateY() {
        let translateY;

        if (this.slideMode === 'up') {
            translateY = 'translateY(100%)';
        } else if (this.slideMode === 'down') {
            translateY = 'translateY(-100%)';
        }
        this.options.style.transition = 'transform .4s';
        this.options.style.transform = translateY;
    }

    open(a11y) {
        const isClippedByViewport = () => {
            // 레이어 하단이 viewport에서 짤리는지 체크
            return this.options.getBoundingClientRect().bottom >= window.innerHeight;
        };

        const onOpenEnd = () => {
            this.classList.add(this.classes.state.open);
            this.options.offsetHeight;
            requestAnimationFrame(() => {
                if (this.slideMode) {
                    this.options.style.transform = 'translateY(0)';
                }

                if (a11y) {
                    const prev = this.options.querySelector(`li.${this.classes.state.active}`) ?? this.options.querySelector(`li:first-child`);
                    if (prev) prev.querySelector('a, input').focus(); // 선택된 항목으로 포커스

                    this.options.addEventListener('keydown', (event) => {
                        const items = Array.from(this.options.querySelectorAll('li'));
                        const focusedElement = event.target.closest('li');
                        const currentIndex = focusedElement ? items.indexOf(focusedElement) : -1;

                        const focusPrevItem = () => {
                            event.preventDefault();
                            if (currentIndex > 0) {
                                const prevItem = items[currentIndex - 1];
                                const prevFocusElement = prevItem.querySelector('a, input');
                                prevFocusElement?.focus();
                            }
                        }

                        const focusNextItem = () => {
                            event.preventDefault();
                            if (currentIndex < items.length - 1) {
                                const nextItem = items[currentIndex + 1];
                                const nextFocusElement = nextItem.querySelector('a, input');
                                nextFocusElement?.focus();
                            }
                        }

                        switch (event.key) {
                            case 'Escape':
                                event.preventDefault();
                                this.close(true);
                                break;
                                
                            case 'ArrowDown':
                                focusNextItem();
                                break;
                                
                            case 'ArrowUp':
                                focusPrevItem();
                                break;
                                
                            case 'Tab':
                                const lastItem = items[items.length - 1];
                                const firstItem = items[0];

                                if (event.shiftKey) {
                                    focusPrevItem();
                                    if (focusedElement === firstItem) this.close(true);
                                    
                                } else if (!event.shiftKey) {
                                    focusNextItem();
                                    if (focusedElement === lastItem) this.close(true);
                                }
                                break;

                            case 'Enter':
                                if (focusedElement && focusedElement.querySelector('input')) {
                                    event.preventDefault();
                                    this.onEnter(event);
                                }
                                break;
                                
                            default:
                                break;
                        }
                    });
                }
            });
        };

        if (this.slideMode) {
            this.setTranslateY();
            setTimeout(() => {
                onOpenEnd();
            }, 50);
        } else {
            onOpenEnd();
        }

        if (isClippedByViewport()) {
            this.classList.add(this.classes.state.reverse);
            this.options.style.bottom = cuiUtils.getComputed.height(this.button) + 'px';
        }
    }

    close(a11y) {
        const onCloseEnd = () => {
            this.classList.remove(this.classes.state.open, this.classes.state.reverse);
            this.options.style.bottom = '';
            this.options.style.transform = '';
            this.options.style.transition = '';

            if (a11y) {
                this.button.focus();
            }
        };

        if (this.slideMode) {
            this.setTranslateY();

            const duration = cuiUtils.getTransitionDuration(this.options);
            setTimeout(() => {
                onCloseEnd();
            }, duration);
        } else {
            onCloseEnd();
        }
    }

    onEnter(e) {
        const target = e.target;

        if (target.closest(`.${this.classes.button}`)) {
            if (this.classList.contains(this.classes.state.open)) {
                this.close(true);
            } else {
                this.open(true);
            }
        }

        if (this.selectMode) {
            const optionItem = target.closest(`.${this.classes.options} li`);
            if (optionItem && !optionItem.classList.contains(this.classes.state.active)) {
                const prev = this.options.querySelector(`li.${this.classes.state.active}`);
                if (prev) {
                    prev.classList.remove(this.classes.state.active);
                    const prevInput = prev.querySelector('input');
                    if (prevInput) prevInput.checked = false;
                }
    
                optionItem.classList.add(this.classes.state.active);
                this.setValue(optionItem);
                this.close(true);

                // 커스텀 이벤트 추가
                this.dispatchChangeEvent(optionItem);
            }
        }
    }

    onLeave(e) {
        const target = e.target;
        if (e.button === 0) {
            // 바닥 페이지 왼클릭시 닫히게
            if (!this.contains(target) && this.classList.contains(this.classes.state.open)) {
                this.close(false);
            }
        }
    }

    setValue(item) { // select mode 이고 선택된 옵션값이 있을때(.is-active), 해당 옵션값으로 버튼명 변경
        const selected = item ? item : this.options.querySelector(`li.${this.classes.state.active}`);
        if (selected) {
            this.button.textContent = selected.textContent;
            const input = selected.querySelector('input');
            if (input) input.checked = true;
        }
    }

    dispatchChangeEvent(item) {
        const customEvent = new CustomEvent('custom-ui-dropdown:change', {
            detail: {
                index: Array.from(this.options.querySelectorAll('li')).indexOf(item),
                element: item.querySelector('a, input'),
                parentElement: item
            },
            bubbles: false
        });

        this.dispatchEvent(customEvent);
    }
}
customElements.define('custom-ui-dropdown', CustomUIDropdown);


/**
 * 공통 컴포넌트
 */
const commonUI = {
    /**
     * 스크롤 애니메이션 설정
     */
    setupScrollAnimation(rootEl = document) {
        let hasScrolled = false;

        const checkElements = () => {
            const scrollElements = rootEl.querySelectorAll(".scroll_ani");
            const windowHeight = window.innerHeight;
            const isTop = window.scrollY === 0;

            if (window.scrollY > 0) {
                hasScrolled = true;
            }

            scrollElements.forEach((element) => {
                const rect = element.getBoundingClientRect();
                const elementTop = rect.top;
                const elementBottom = rect.bottom;
                
                // 요소가 화면에 완전히 사라졌는지 확인
                const isCompletelyHidden = elementBottom < 0 || elementTop > windowHeight;
                
                // 요소가 화면에 조금이라도 보이는지 확인
                const isPartiallyVisible = elementBottom > 0 && elementTop < windowHeight;
                
                // 요소가 화면의 50% 이상 보이는지 확인
                const isHalfVisible = (
                    (elementTop < windowHeight * 0.5 && elementBottom > windowHeight * 0.5) || // 요소가 화면 중앙을 지나가는 경우
                    (elementTop >= 0 && elementTop < windowHeight * 0.5) || // 요소가 화면 상단에서 시작하는 경우
                    (elementBottom > windowHeight * 0.5 && elementBottom <= windowHeight) // 요소가 화면 하단에서 끝나는 경우
                );

                /*
                * scroll-ani--type2 :: window load 시 element가 조금이라도 보이면 active 추가, 
                * 스크롤 이벤트 발생 후 화면 최 상단으로 오면 active 제거
                */ 
                if (element.classList.contains("scroll-ani--type2")) {
                    if (isTop && hasScrolled) {
                        element.classList.remove("active");
                    } else if (isPartiallyVisible) {
                        element.classList.add("active");
                    } else if (isCompletelyHidden) {
                        element.classList.remove("active");
                    }
                } else {
                    if (isHalfVisible) {
                        element.classList.add("active");
                    } else if (isCompletelyHidden) {
                        element.classList.remove("active");
                    }
                }
            });
        };

        // 초기 로드 시 체크
        checkElements();

        // window load 시 한번 더 체크
        window.addEventListener("load", checkElements);

        // 스크롤 이벤트에 핸들러 추가
        window.addEventListener("scroll", checkElements);
    },

    // 스크롤 애니메이션 공통 초기화
    initScrollAnimation() {
        this.setupScrollAnimation();

        if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:section:load', (event) => {
                if (event.target.classList.contains('shopify-section--scroll-ani')) {
                    this.setupScrollAnimation(event.target);
                }
            });
        }
    }
};

window.onload = () => {
    commonUI.initScrollAnimation();
};

/**
 * 메인 페이지용 컴포넌트
 */
const pageMain = {
    /**
     * 메인 KV 배너 초기화 함수
     * @param {HTMLElement|Document} rootEl - 배너를 검색할 루트 요소
     */
    kvBanner: function (rootEl = document) {
        const kvBanners = rootEl.querySelectorAll(".main-kv-banner");

        // 배너가 없으면 함수 종료
        if (!kvBanners.length) return;

        // 각 배너에 대해 스와이퍼 초기화
        Array.from(kvBanners).forEach((banner, index) => {
            const swiperId = "mainkvSwiper" + index;
            const swiperElement = banner.querySelector(".main_kv_swiper");

            // 관련 요소들 선택
            const progressBar = banner.querySelector(".main_kv_progress_bar");
            const controller = banner.querySelector(".main_kv_controller");
            const arrowKey = banner.querySelector(".main_kv_arrowkey");
            const pauseButton = banner.querySelector(".main_kv_pause");
            const pagination = banner.querySelector(".main_kv_pagination");
            const slides = swiperElement.querySelectorAll(".swiper-slide");

            if (slides.length > 1) {
                let main_kv_time = 10000;

                // 2개 이상의 슬라이드가 있을 때만 스와이퍼 초기화
                window[swiperId] = new Swiper(swiperElement, {
                    speed: 500,
                    loop: true,
                    autoplay: {
                        delay: main_kv_time,
                        disableOnInteraction: false,
                    },
                    navigation: {
                        nextEl: banner.querySelector(".main_kv_next"),
                        prevEl: banner.querySelector(".main_kv_prev"),
                    },
                    touchEventsTarget: "swiper-wrapper",
                    on: {
                        // 슬라이드 전환 완료 후 이벤트
                        slideChangeTransitionEnd: function() {
                            const activeSlide = swiperElement.querySelector(".swiper-slide-active");
                            const active_num = Array.from(slides).indexOf(activeSlide);

                            // slide data-col === 'dark' 일 때 .main-kv-banner 에 .color-white 클래스 추가 됨
                            if (activeSlide.getAttribute("data-col") === "dark") {
                                banner.classList.add("color-white");
                            } else {
                                banner.classList.remove("color-white");
                            }

                            if (active_num % 2 == 0) {
                                const progressItem = progressBar.querySelector("p:nth-child(2)");
                                progressItem.classList.add("active");
                                progressItem.style.transitionDuration = main_kv_time + "ms";

                                Array.from(progressBar.querySelectorAll("p:not(:nth-child(2))")).forEach((item) => {
                                    item.style.transitionDuration = "0ms";
                                    item.classList.remove("active");
                                });
                            } else {
                                const progressItem = progressBar.querySelector("p:nth-child(1)");
                                progressItem.classList.add("active");
                                progressItem.style.transitionDuration = main_kv_time + "ms";

                                Array.from(progressBar.querySelectorAll("p:not(:nth-child(1))")).forEach((item) => {
                                    item.style.transitionDuration = "0ms";
                                    item.classList.remove("active");
                                });
                            }

                            // 활성 슬라이드의 링크 탭 접근성 설정
                            const activeLinks = activeSlide.querySelectorAll("a");
                            if (activeLinks.length) {
                                activeLinks.forEach((link) => link.setAttribute("tabindex", "0"));

                                const inactiveLinks = swiperElement.querySelectorAll(
                                    ".swiper-slide:not(.swiper-slide-active) a",
                                );
                                inactiveLinks.forEach((link) => link.setAttribute("tabindex", "-1"));
                            }
                        },

                        // 활성 슬라이드 변경 시 이벤트
                        activeIndexChange: function () {
                            const num = this.activeIndex;
                            const currentSlide = this.slides[num];

                            // 비디오 처리
                            if (currentSlide.getElementsByTagName("video").length) {
                                const video = currentSlide.getElementsByTagName("video")[0];
                                video.currentTime = 0;
                                video.play();
                            } else {
                                const videos = swiperElement.querySelectorAll(".swiper-slide .video video");
                                videos.forEach((video) => {
                                    if (!video.paused) {
                                        video.currentTime = 0;
                                        video.pause();
                                    }
                                });
                            }

                            // 헤더 색상 처리
                            const header = document.querySelector("#header.custom-header");
                            if (header && header.classList.contains("is-dark")) {
                                header.classList.remove("is-dark");
                            }

                            const dataCol = currentSlide.getAttribute("data-col");
                            if (header && dataCol === 'dark') {
                                header.classList.add('is-dark');
                            }
                        },
                    },

                    // 페이지네이션 커스텀 렌더링
                    pagination: {
                        el: pagination,
                        type: "custom",
                        renderCustom: function (swiper, current, total) {
                            let formattedCurrent = current < 10 ? "0" + current : current;
                            let formattedTotal = total < 10 ? "0" + total : total;

                            return (
                                '<span class="num_current">' +
                                formattedCurrent +
                                "</span>" +
                                '<span class="num_total">' +
                                formattedTotal +
                                "</span>"
                            );
                        },
                    },
                });

                // 초기 프로그레스바 설정
                const firstProgress = progressBar.querySelector("p:nth-child(1)");
                if (firstProgress) {
                    firstProgress.classList.add("active");
                    firstProgress.style.transitionDuration = main_kv_time + "ms";
                }

                // 슬라이드 재생/정지 토글 버튼
                if (pauseButton) {
                    pauseButton.setAttribute("data-swiper-id", swiperId);
                    pauseButton.setAttribute("data-active", "true");

                    pauseButton.addEventListener("click", function () {
                        const activeSwiperName = this.getAttribute("data-swiper-id");
                        const activeSwiper = window[activeSwiperName];
                        const isActive = this.getAttribute("data-active") === "true";

                        if (isActive) {
                            // 슬라이드 일시정지
                            activeSwiper.autoplay.stop();
                            this.setAttribute("data-active", "false");
                            this.innerHTML = "PLAY";
                            this.classList.add("pause");
                            progressBar.classList.add("pause");
                        } else {
                            // 슬라이드 재생
                            activeSwiper.autoplay.start();
                            this.setAttribute("data-active", "true");
                            this.innerHTML = "PAUSE";
                            this.classList.remove("pause");
                            progressBar.classList.remove("pause");
                        }
                    });
                }
            } else if (slides.length === 1) {
                // 슬라이드가 하나만 있는 경우
                if (controller) controller.style.display = "none";
                if (arrowKey) arrowKey.style.display = "none";
                slides[0].classList.add("swiper-slide-active");
            }
        });
    },

    /**
     * 스크롤 버튼 초기화 - 다음 섹션으로 이동
     * @param {Element} rootEl - 상위 요소
     */
    scrollToNextSection(rootEl = document) {
        const $scrollButtons = $(rootEl).find(".btn_scroll");

        $scrollButtons.off("click").on("click", function () {
            const $currentSection = $(this).closest(".shopify-section");
            const $nextSection = $currentSection.next(".shopify-section");

            if ($nextSection.length) {
                // 헤더 높이를 고려하여 스크롤 위치 조정
                const headerHeight = $(".custom-header__wrap .page-width").outerHeight() || 0;
                const headerHeightTop = $(".custom-header__top").outerHeight() || 0;
                const isMobile = window.innerWidth <= 1280;
                
                const headerHeightFixHeight = isMobile ? headerHeight : headerHeight - headerHeightTop;
                const nextSectionTop = $nextSection.offset().top - headerHeightFixHeight;

                $("html, body").animate({ 
                    scrollTop: nextSectionTop 
                }, 800);
            }
        });
    },

    ourServiceBanner(rootEl = document) {
        // 모든 etcservices_swiper 요소 찾기
        const swiperElements = rootEl.querySelectorAll(".etcservices_swiper");

        // 각 스와이퍼 요소에 대해 개별적으로 초기화
        swiperElements.forEach(function (swiperElement) {
            new Swiper(swiperElement, {
                slidesPerView: 3,
                spaceBetween: 40,
                breakpoints: {
                    320: {
                        slidesPerView: 1.2,
                        spaceBetween: 15,
                        centeredSlides: true,
                    },
                    769: {
                        slidesPerView: 3,
                        spaceBetween: 40,
                        centeredSlides: false,
                    },
                },
            });
        });
    },
};

const component = {
    collection: function (rootEl = document) {
        const swiperElements = rootEl.querySelectorAll(".component-collection__swiper");

        swiperElements.forEach(function (swiperElement) {
            // 기존 Swiper 인스턴스가 있다면 제거
            if (swiperElement.swiper) {
                swiperElement.swiper.destroy(true, true);
            }

            // 슬라이드 개수 확인
            const slideCount = swiperElement.querySelectorAll('.swiper-slide').length;
            
            // 컨트롤러 요소
            const controller = swiperElement.querySelector('.component-collection__controller');
            
            // 화면 너비에 따른 컨트롤러 표시 여부 설정
            const handleControllerVisibility = () => {
                if (controller) {
                    if (window.innerWidth <= 768) {
                        controller.style.display = slideCount > 1 ? '' : 'none';
                    } else {
                        controller.style.display = slideCount > 4 ? '' : 'none';
                    }
                }
            };

            // 초기 실행
            handleControllerVisibility();

            // 리사이즈 이벤트에 대한 처리
            window.addEventListener('resize', handleControllerVisibility);
            
            // 새로운 Swiper 인스턴스 생성
            new Swiper(swiperElement, {
                breakpoints: {
                    320: {
                        slidesPerView: 1,
                        slidesPerGroup: 1,
                    },
                    769: {
                        slidesPerView: 4,
                        slidesPerGroup: 4,
                        spaceBetween: 40,
                    },
                },
                pagination: {
                    el: ".component-collection__controller-pagination",
                    type: "fraction",
                },
                navigation: {
                    nextEl: ".component-collection__controller-next",
                    prevEl: ".component-collection__controller-prev",
                },
            });
        });
    },

    /**
     * TODO :: item click 시 window top 으로 이동하는 부분 수정
     * 
     */
    plpCategoryNavigator: function (rootEl = document) {
        const section = rootEl.querySelector('.shopify-section--plp-category-navigator') || rootEl;
        if (!section) return;

        const containers = section.querySelectorAll('.plp-category-navigator__inner');
        containers.forEach(container => {
            const items = container.querySelectorAll('.plp-category-navigator__item');
            let isDragging = false;
            let startPosition = 0;
            let startScrollLeft = 0;

            // 아이템을 중앙으로 스크롤하는 함수
            function scrollItemToCenter(item) {
                const containerWidth = container.offsetWidth;
                const itemWidth = item.offsetWidth;
                const itemLeft = item.offsetLeft;
                const centerPosition = itemLeft - (containerWidth / 2) + (itemWidth / 2);

                container.scrollTo({
                    left: centerPosition,
                    behavior: 'smooth'
                });
            }

            const headerHeight = document.querySelector('.custom-header').offsetHeight;
            const isMobile = window.innerWidth <= 768;

            // 768px 이하일 때만 스크롤 동작 적용
            if (isMobile) {
                // 현재 스크롤 위치가 navigator 섹션에 있는지 확인
                const navigatorSection = document.querySelector('.shopify-section--plp-category-navigator');
                if (navigatorSection) {
                    const navigatorRect = navigatorSection.getBoundingClientRect();
                    // navigator 섹션이 화면 상단에 보이는지 확인 (헤더 높이를 고려)
                    if (navigatorRect.top > headerHeight) {
                        setTimeout(() => {
                            const navigatorTop = navigatorSection.offsetTop - headerHeight;
                            window.scrollTo({
                                top: navigatorTop,
                                behavior: 'smooth'
                            });
                        }, 500);
                    }
                }
            }

            // 현재 활성화된 아이템을 중앙으로 스크롤
            const activeItem = container.querySelector('.plp-category-navigator__item.active');
            if (activeItem) {
                scrollItemToCenter(activeItem);
            }

            function handleMouseDown(e) {
                e.preventDefault();
                isDragging = true;
                startPosition = e.pageX;
                startScrollLeft = container.scrollLeft;
                container.style.cursor = 'grabbing';
            }

            function handleMouseMove(e) {
                if (!isDragging) return;
                const dx = e.pageX - startPosition;
                container.scrollLeft = startScrollLeft - dx;
            }

            function handleMouseUp(e) {
                if (isDragging) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                isDragging = false;
                container.style.cursor = '';
            }

            // 링크 클릭 이벤트 처리
            container.querySelectorAll('a').forEach(link => {
                link.addEventListener('click', (e) => {
                    if (isDragging) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }, true);
            });

            // 마우스 이벤트
            container.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            container.addEventListener('mouseleave', handleMouseUp);

            // 모바일 터치 이벤트
            // container.addEventListener('touchstart', (e) => {
            //     isDragging = true;
            //     startPosition = e.touches[0].pageX;
            //     startScrollLeft = container.scrollLeft;
            // }, { passive: true });

            // container.addEventListener('touchmove', (e) => {
            //     if (!isDragging) return;
            //     const dx = e.touches[0].pageX - startPosition;
            //     container.scrollLeft = startScrollLeft - dx;
            //     e.preventDefault();
            // }, { passive: false });

            // container.addEventListener('touchend', () => {
            //     isDragging = false;
            // });
        });
    }
};
